import { deleteField, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import store from '@store/index';
import CashFlowServ from '@api/CashFlows/CashFlowServ';
import CashRegisterServ from '@api/CashRegister/CashRegisterServ';
import { CashFlow, cashFlowsRef } from '@api/CashFlows/CashFlow';
import UserServ from '@api/Users/UserServ';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import {
    describeCanceledExpense,
    describeEditedExpense,
    describePurchaseSynced,
    EXPENSE_ACTIVITY_SECTION
} from '@api/ActivityLogs/ExpenseActivity';
import InventoryServ from '@api/Inventory/InventoryServ';
import { getCurrentPermissionSubject } from '@utils/Permissions';
import { isActiveCashRegister } from '@utils/CashRegisters';
import { recurringExpensesRef, recurringInstanceId, RecurringAmountMode } from '@api/RecurringExpenses/RecurringExpense';
import RecurringExpenseServ from '@api/RecurringExpenses/RecurringExpenseServ';
import {
    buildExpenseCashFlow,
    buildExpenseEdit,
    buildRecurringExpense,
    canMakeRecurring,
    ExpenseDraft,
    findRecentPayroll,
    validateExpense,
    validateExpenseCancel,
    validateExpenseEdit
} from '@utils/Expenses';
import { countsInCashCount } from '@utils/CashFlows';

/** ¿Hay caja abierta de hoy en ESA sucursal? Solo se sabe de la sucursal en operación. */
const hasOpenCashRegisterIn = (branchOfficeId?: string): boolean => {
    const { auth, krispis } = store.getState();
    if (!branchOfficeId || branchOfficeId !== auth.branchOfficeSelected?.id) return false;
    return isActiveCashRegister(krispis.cashRegister, branchOfficeId);
};

/** Quién hace el cambio, para dejarlo en el documento. */
const currentAuthor = async (): Promise<{ uid: string; name: string }> => {
    const user = await UserServ.getUser();
    return { uid: user.uid, name: user.profile?.name || user.email || '' };
};

/** La bitácora observa: nunca impide que el cambio se haga. */
const logExpense = async (flowId: string, description: string): Promise<void> => {
    if (!description) return;
    await ActivityLogServ.addActivityLog(EXPENSE_ACTIVITY_SECTION, flowId, description);
};

export default {
    /**
     * El ÚNICO camino para registrar un gasto con categoría (plan de Gastos F2).
     * **El gate vive aquí, no en la pantalla**: el rol, la categoría, la caja abierta
     * y los datos que pide la categoría se revisan antes de escribir, venga de
     * donde venga la llamada. Quién puede qué: `@utils/Expenses` +
     * `Capabilities`. Es UNA escritura (`saveCashFlow`).
     *
     * `branchOfficeId`: desde el Informe se registra para otra sucursal (la renta de
     * Cholula). Sacar dinero de la caja solo se puede en la sucursal en operación y
     * con su caja abierta: de otra sucursal no se sabe si la hay.
     */
    async saveExpense(
        draft: ExpenseDraft,
        options: { branchOfficeId?: string; recurring?: RecurringAmountMode } = {}
    ): Promise<CashFlow> {
        const branchOfficeId = options.branchOfficeId || store.getState().auth.branchOfficeSelected?.id;
        const subject = getCurrentPermissionSubject();
        const problem = validateExpense(draft, {
            subject,
            branchOfficeId,
            // Solo lo que sale de la caja necesita la caja: ahí se confirma contra
            // Firestore (1 lectura), no contra la copia de la sesión.
            hasOpenCashRegister: draft.paidFromCash
                ? await CashRegisterServ.confirmActiveCashRegister(branchOfficeId)
                : hasOpenCashRegisterIn(branchOfficeId)
        });
        if (problem) throw new Error(problem);
        if (!options.recurring) {
            const saved = await CashFlowServ.saveCashFlow(buildExpenseCashFlow(draft), { branchOfficeId });
            // El gasto YA quedó guardado; el inventario lo alcanza (ahora o con el aseo).
            return this.syncPurchaseInventory(saved);
        }
        // Gasto fijo (§3.4): la instancia de este mes con id determinista y, DESPUÉS,
        // la plantilla. Si la plantilla fallara, el gasto del mes ya quedó registrado.
        if (!branchOfficeId || !canMakeRecurring(draft.category, subject, branchOfficeId)) {
            throw new Error('Tu perfil no puede registrar gastos fijos que se repiten');
        }
        const author = await currentAuthor();
        const template = buildRecurringExpense(draft, {
            id: doc(recurringExpensesRef).id,
            mode: options.recurring,
            branchOfficeId,
            author,
            now: new Date()
        });
        const instance = await CashFlowServ.saveCashFlow(
            { ...buildExpenseCashFlow(draft), recurringExpenseId: template.id },
            { branchOfficeId, id: recurringInstanceId(template.id, template.startPeriod) }
        );
        await RecurringExpenseServ.createRecurringExpense(template);
        return instance;
    },

    /**
     * La nómina que ya se le registró a esta persona en los últimos días, para avisar
     * antes de registrar otra (`findRecentPayroll`). Query de UN campo, sin índice:
     * lee los pagos de nómina de ESA persona (~4 al mes; a 3 años, ~150 por consulta) y
     * solo cuando se captura una nómina. Si falla, no hay aviso: nunca bloquea.
     */
    async getRecentPayroll(payrollUserId: string): Promise<CashFlow | undefined> {
        try {
            const snapshot = await getDocs(query(cashFlowsRef, where('payrollUserId', '==', payrollUserId)));
            const flows = snapshot.docs.map((d) => ({ ...(d.data() as CashFlow), id: d.id }));
            return findRecentPayroll(flows, payrollUserId, new Date());
        } catch {
            return undefined;
        }
    },

    /**
     * ¿El corte que contiene este gasto ya cerró? Entonces guardó su esperado y el
     * monto ya no se toca (§4.1). Un gasto fuera de toda caja (o si la consulta
     * falla) no se bloquea: editar nunca debe quedar atrapado por la bitácora.
     * Lecturas: 1 consulta de cajas de ±1 día de la sucursal (~2–6 docs).
     */
    async isAmountLocked(flow: CashFlow): Promise<boolean> {
        try {
            const register = await CashFlowServ.findCashRegisterOfFlow(flow);
            return !!register?.closedAt;
        } catch {
            return false;
        }
    },

    /** Edita un gasto con las reglas de §4.1. Deja renglón "antes → después". UNA escritura. */
    async updateExpense(flow: CashFlow, draft: ExpenseDraft): Promise<CashFlow> {
        if (!flow.id) throw new Error('El gasto no tiene id');
        const amountLocked = await this.isAmountLocked(flow);
        const problem = validateExpenseEdit(flow, draft, { subject: getCurrentPermissionSubject(), amountLocked });
        if (problem) throw new Error(problem);
        const author = await currentAuthor();
        const { changes, removed } = buildExpenseEdit(flow, draft);
        await updateDoc(doc(cashFlowsRef, flow.id), {
            ...changes,
            ...Object.fromEntries(removed.map((field) => [field, deleteField()])),
            editedAt: new Date(),
            editedBy: author.uid,
            editedByName: author.name
        });
        const after: CashFlow = { ...flow, ...changes };
        removed.forEach((field) => delete (after as Partial<CashFlow>)[field]);
        await logExpense(flow.id, describeEditedExpense(flow, after));
        return this.syncPurchaseInventory(after);
    },

    /**
     * Iguala el ledger de inventario a este gasto (plan de inventario F1): crear,
     * corregir y cancelar una compra de insumo pasan por aquí. **El gasto ya está
     * guardado cuando esto corre y nunca se deshace por el inventario**: si el server
     * no contesta (escala a cero), el gasto se queda `inventorySyncPending` y el aseo
     * diario lo reintenta. Devuelve el gasto con lo que el server dejó, o tal cual (con
     * el pendiente) para que la pantalla lo diga. Nunca lanza.
     */
    async syncPurchaseInventory(flow: CashFlow): Promise<CashFlow> {
        if (!flow.id || !flow.inventorySyncPending) return flow;
        try {
            const result = await InventoryServ.syncPurchase(flow.id, store.getState().auth.loggedUser?.uid);
            if (result.status === 'synced') {
                await logExpense(flow.id, describePurchaseSynced(result.items));
            }
            const synced: CashFlow = { ...flow, ...(result.inventory ? { inventory: result.inventory } : {}) };
            delete synced.inventorySyncPending;
            return synced;
        } catch (error) {
            // Queda pendiente: lo dice la pantalla y lo reintenta el aseo (tarea 7d del server)
            console.error('La compra de insumo no entró al inventario; queda pendiente', flow.id, error);
            return { ...flow, inventorySyncPending: true, inventorySyncError: errorMessage(error) };
        }
    },

    /**
     * Cancela un gasto: **nunca se borra**. Queda a la vista, tachado, y deja de contar
     * en el corte, la lista y el Estado de Resultados (`isActiveFlow`). El PIN lo pide
     * la pantalla; el motivo y el permiso se revisan aquí. UNA escritura + bitácora
     * (en el gasto y, si restaba en una caja, también en la caja).
     */
    async cancelExpense(flow: CashFlow, reason: string): Promise<CashFlow> {
        if (!flow.id) throw new Error('El gasto no tiene id');
        const problem = validateExpenseCancel(flow, reason, getCurrentPermissionSubject());
        if (problem) throw new Error(problem);
        const author = await currentAuthor();
        // Una compra que entró al inventario tiene que salir: queda pendiente y se iguala abajo
        const hadPurchase = flow.purchaseQuantity !== undefined || !!flow.inventory;
        await updateDoc(doc(cashFlowsRef, flow.id), {
            status: 'deleted',
            deletedAt: new Date(),
            deletedBy: author.uid,
            deletedByName: author.name,
            deleteReason: reason.trim(),
            ...(hadPurchase ? { inventorySyncPending: true } : {})
        });
        const description = describeCanceledExpense(flow, reason);
        await logExpense(flow.id, description);
        if (countsInCashCount(flow)) {
            try {
                const cajaId = await CashFlowServ.findCashRegisterForFlow(flow);
                if (cajaId) await ActivityLogServ.addActivityLog('cashRegister', cajaId, description);
            } catch {
                // La bitácora observa: jamás impide cancelar.
            }
        }
        return this.syncPurchaseInventory({
            ...flow,
            status: 'deleted',
            deleteReason: reason.trim(),
            ...(hadPurchase ? { inventorySyncPending: true } : {})
        });
    }
};

const errorMessage = (error: unknown): string => {
    const response = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
    if (response) return response;
    return error instanceof Error ? error.message : String(error);
};
